import React, { useEffect, useMemo, useState } from "react";
import { getWarehouseStock, WarehouseStockDto } from "../../../api/warehouseStock";
import { getCategories, ProductCategoryDto } from "../../../api/productCategories";

import StockTopBar from "./StockTopBar";
import StockTable from "./StockTable";
import { printHtml, escapeHtml } from "../../../utils/print";

import ReceiptModal from "../modals/ReceiptModal";
import IssueModal from "../modals/IssueModal";

export default function StockTab() {
  const [stock, setStock] = useState<WarehouseStockDto[]>([]);
  const [categories, setCategories] = useState<ProductCategoryDto[]>([]);

  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState<"all" | "product" | "service">("all");
  const [filterStock, setFilterStock] = useState<"all" | "in" | "out">("all");
  const [filterCategory, setFilterCategory] = useState<number | "all">("all");

  const [showReceipt, setShowReceipt] = useState(false);
  const [showIssue, setShowIssue] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);

  async function loadStock() {
    const res = await getWarehouseStock();
    setStock(res ?? []);
  }

  async function loadCategories() {
    const res = await getCategories();
    setCategories(res ?? []);
  }

  useEffect(() => {
    loadStock();
    loadCategories();
  }, []);

  const resetFilters = () => {
    setSearch("");
    setFilterType("all");
    setFilterStock("all");
    setFilterCategory("all");
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();

    return stock.filter((s) => {
      const name = (s.product.nameRu ?? "").toLowerCase();
      const sku = (s.product.sku ?? "").toLowerCase();
      const type = s.product.type;
      const qty = s.quantity ?? 0;

      const matchesSearch = !q || name.includes(q) || sku.includes(q);

      const matchesType =
        filterType === "all"
          ? true
          : filterType === "product"
          ? type === "product"
          : type === "service";

      const matchesStock =
        filterStock === "all"
          ? true
          : filterStock === "in"
          ? (type === "service" || qty > 0)
          : (type === "product" && qty === 0);

      const matchesCategory =
        filterCategory === "all"
          ? true
          : s.product.categoryId === filterCategory;

      return matchesSearch && matchesType && matchesStock && matchesCategory;
    });
  }, [stock, search, filterType, filterStock, filterCategory]);

  const openReceipt = (product: any) => {
    setSelectedProduct(product);
    setShowReceipt(true);
  };

  const openIssue = (product: any) => {
    setSelectedProduct(product);
    setShowIssue(true);
  };

  const onSuccess = () => {
    loadStock();
  };

  const printStock = () => {
    const rows = filtered
      .filter((s) => s.product.type !== "service")
      .slice()
      .sort((a, b) => (a.product.nameRu ?? "").localeCompare(b.product.nameRu ?? "", "ru"));
    const body =
      `<table><thead><tr><th>№</th><th>SKU</th><th>Наименование</th><th class="num">Остаток</th></tr></thead><tbody>` +
      rows
        .map(
          (s, i) =>
            `<tr><td>${i + 1}</td><td>${escapeHtml(s.product.sku || "—")}</td><td>${escapeHtml(s.product.nameRu)}</td><td class="num">${Number(s.quantity ?? 0)}</td></tr>`,
        )
        .join("") +
      `</tbody></table>`;
    printHtml("Остатки на складе", body, `Позиций: ${rows.length}.`);
  };

  return (
    <div className="warehouse-stock-tab">
      <div className="stock-print-row">
        <button className="stock-print-btn" onClick={printStock}>
          🖨 Печать остатков
        </button>
      </div>
      <StockTopBar
        search={search}
        onSearchChange={setSearch}
        filterType={filterType}
        onFilterTypeChange={setFilterType}
        filterStock={filterStock}
        onFilterStockChange={setFilterStock}
        filterCategory={filterCategory}
        onFilterCategoryChange={setFilterCategory}
        categories={categories}
        onResetFilters={resetFilters}
      />

      <StockTable
        items={filtered}
      />

      {showReceipt && (
        <ReceiptModal
            id={null}
            onClose={() => setShowReceipt(false)}
            onSaved={onSuccess}
            />

      )}

      {showIssue && (
        <IssueModal
          id={null}
          onClose={() => setShowIssue(false)}
          onSaved={onSuccess}
        />
      )}
    </div>
  );
}
